import { LayoutProject } from '../types';
import { STORAGE_KEYS } from '../constants';
import { isLayout3DProject } from './layoutImport';
import type { Layout3DProject } from './layout3dGenerate';
import { classifyProjectFile } from './projectExport';
import type { EquipmentDoc } from '../features/equipment/equipmentTypes';

export type ImportedLayoutFile = LayoutProject | Layout3DProject;
export type ParsedProjectFile =
  | { type: 'equipment'; doc: EquipmentDoc }
  | { type: 'fab'; project: LayoutProject }
  | { type: 'layout'; project: ImportedLayoutFile };

/**
 * Save project to localStorage
 */
export const saveToLocalStorage = (key: string, data: unknown): void => {
  try {
    const serialized = JSON.stringify(data);
    localStorage.setItem(key, serialized);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Load project from localStorage
 */
export const loadFromLocalStorage = <T>(key: string): T | null => {
  try {
    const serialized = localStorage.getItem(key);
    if (serialized === null) return null;
    return JSON.parse(serialized) as T;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

/**
 * Remove item from localStorage
 */
export const removeFromLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

/**
 * Export project as JSON file
 */
export const exportToJSON = (project: LayoutProject, filename: string = 'layout'): void => {
  try {
    const json = JSON.stringify(project, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting to JSON:', error);
    throw error;
  }
};

/**
 * Import project from JSON file (layout, combined fab-project, or equipment-only).
 */
export const importFromJSON = (file: File): Promise<ParsedProjectFile> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const project = JSON.parse(content) as unknown;

        if (isLayout3DProject(project)) {
          resolve({ type: 'layout', project });
          return;
        }

        resolve(classifyProjectFile(project));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsText(file);
  });
};

/**
 * Create autosave
 */
export const createAutosave = (project: LayoutProject): void => {
  saveToLocalStorage(STORAGE_KEYS.AUTOSAVE, {
    ...project,
    metadata: {
      ...project.metadata,
      autosaveTimestamp: new Date().toISOString(),
    },
  });
};

/**
 * Load autosave
 */
export const loadAutosave = (): LayoutProject | null => {
  return loadFromLocalStorage<LayoutProject>(STORAGE_KEYS.AUTOSAVE);
};

/**
 * Clear autosave
 */
export const clearAutosave = (): void => {
  removeFromLocalStorage(STORAGE_KEYS.AUTOSAVE);
};

/**
 * Check if autosave exists
 */
export const hasAutosave = (): boolean => {
  return loadAutosave() !== null;
};
